import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormControl,
	Validators,
} from '@angular/forms';
import { Subscription as RxJsSubscription } from 'rxjs';
import {
	ErrorService,
	ExplainErrorsService,
	SessionService,
} from '@app/services';
import { Role } from '../role';
import { RoleService } from '../role.service';
@Component({
	selector: 'hpf-role-form',
	templateUrl: './role-form.component.html',
})
export class RoleFormComponent implements OnInit, OnDestroy {
	/** The model subscription */
	private modelSubscription: RxJsSubscription;
	/** The role to inject in the form */
	@Input() role: Role;
	/** Called the save button is clicked for a new instance */
	@Output() create = new EventEmitter<Role>();
	/** Called the save button is clicked for an existing instance */
	@Output() update = new EventEmitter<void>();
	/** Called the delete button is clicked */
	@Output() delete = new EventEmitter<void>();
	/** Set loading state of the form */
	@Input() loading = false;
	/** Enable the deletion button */
	@Input() deletable = true;
	/** Denotes if deltion in progress */
	deleting = false;
	/** Show delete modal */
	deleteModal = false;
	/** The form group to use */
	form: FormGroup;
	/** Denotes if the form is pending */
	saving = false;
	/** Check permission of update **/
	readOnly = false;
	/** Constructor */
	constructor(
		private formBuilder: FormBuilder,
		private errorService: ErrorService,
		public explainErrorsService: ExplainErrorsService,
		private roleService: RoleService,
		private sessionService: SessionService
	) {}
	/** Init */
	async ngOnInit() {
		this.readOnly = !(await this.sessionService.checkPermission(
			'role',
			'update'
		));
		// Create form
		this.form = this.formBuilder.group({
			name: new FormControl(this.role.props.name, [Validators.required]),
			admin: new FormControl(this.role.props.admin, [
				Validators.required,
			]),
		});
		// force admin field to always be true
		this.form.controls['admin'].setValue(true);

		// Update form when model loads or changes
		this.modelSubscription = this.role.subscribe(() => {
			this.updateForm();
		});
	}
	/** Destroy */
	ngOnDestroy() {
		this.modelSubscription.unsubscribe();
	}
	/** Called on form submit */
	async onSubmit(): Promise<void> {
		// Saving flag
		this.saving = true;
		try {
			// Update model
			this.updateModel();
			// Creation or update ?
			if (this.role.isNew()) {
				// Creation
				const role: Role = await this.roleService.create(
					this.role.toPayload()
				);
				this.create.next(role);
			} else {
				// Update
				await this.roleService.update(
					this.role.getId(),
					this.role.toPayload()
				);
				this.update.next();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
		// Saving flag
		this.saving = false;
	}
	/** Update models properties from inputs values */
	private updateModel(): void {
		for (const key of Object.keys(this.form.controls)) {
			this.role.props[key] = this.form.get(key).value;
		}
	}
	/** Update inputs values from models properties */
	private updateForm(): void {
		this.form.setValue({
			name: this.role.props.name,
			admin: this.role.props.admin,
		});
	}
	/** Called on deletion */
	onDelete(): void {
		this.deleting = true;
		this.roleService
			.remove(this.role.getId())
			.then(() => {
				this.delete.next();
			})
			.catch((error) => this.errorService.handle(error))
			.then(() => (this.deleting = false));
	}
}
